import { ISensor } from "./Sensor"
import { IProduct, ProductType, ProductUsage } from "./Product"
import { IGlass, otherThickness } from "./Glass"
import { IGlassCustomization, doubleSurfaces, surfaces } from "./Customization"
import { ICustomer } from "./Customer"
import { IStatus } from "./Status"
import { IPrinting } from "./Printing"
import { generateLayers, ILayers } from "./Layers"
import { defaultFormValues } from "./Form"

export interface ISolution {
  product: IProduct
  glass: IGlass
  customization: IGlassCustomization
  printing: IPrinting
  sensor: ISensor
  customer: ICustomer
  status: IStatus
  layers: ILayers
}

export interface IRange<T> {
  min?: T // including
  max?: T // including
}
export type INumericRange = IRange<number>

export function isRange(value: any): value is IRange<any> {
  return "min" in value || "max" in value
}

export function isNumericRange(value: any): value is INumericRange {
  return (
    isRange(value) &&
    typeof value.min === "number" &&
    typeof value.max === "number"
  )
}

/**
 * Defines constraints for all attributes of an object of type T.
 * An attribute can either be either numeric or value-based (e.g., enum).
 * Numeric attributes can be constrained to a range of [min, max] (incl.).
 * Value-based attributes can be constrained to a specific set of values.
 * Constraining each attribute is optional.
 */
type IConstraints<T> = {
  [Attribute in keyof T]?: T[Attribute] extends number
    ? INumericRange
    : T[Attribute][]
}
/**
 * Defines constraints for each top-level attribute of ISolution.
 * Constraining each attribute is optional.
 */
export type ISolutionConstraints = {
  [Attribute in keyof ISolution]?: IConstraints<ISolution[Attribute]>
}

export const defaultConstraints = {
  product: {
    pieces: { min: 1 },
  },
  sensor: {
    diagonal: { min: 1 },
  },
} as ISolutionConstraints

export const FortouchOFixedValues = {
  glass: {
    isDouble: true,
  },
  customization: {
    otherSettings: {
      PF: false,
    },
  },
} as Partial<ISolution>

export const FortouchOConstraints = {
  ...defaultConstraints,
  product: {
    usage: [ProductUsage.Outdoor, ProductUsage.Indoor],
  },
  glass: {
    thickness: [4.3, 5.3, 6.3, 7.3, 8.3, otherThickness],
  },
  customization: {
    surface: doubleSurfaces,
  },
  sensor: {
    diagonal: { min: 7, max: 110 },
  },
} as ISolutionConstraints

export const FortouchSFixedValues = {
  glass: {
    isDouble: false,
  },
  customization: {},
} as Partial<ISolution>

export const FortouchSConstraints = {
  ...defaultConstraints,
  product: {
    usage: [ProductUsage.Indoor, ProductUsage.Outdoor],
  },
  glass: {
    thickness: [1, 2, 3, 4, 5, 6, otherThickness],
  },
  customization: {
    surface: surfaces,
  },
  sensor: {
    diagonal: { min: 7, max: 86 },
  },
} as ISolutionConstraints

export const FortouchCFixedValues = {
  glass: {
    isDouble: false,
  },
  customization: {},
} as Partial<ISolution>

export const FortouchCConstraints = {
  ...defaultConstraints,
  product: {
    usage: [
      ProductUsage.Indoor,
      ProductUsage.Outdoor,
      ProductUsage.SemiOutdoor,
    ],
  },
  glass: {
    thickness: [1, 2, 3, 4, 5, 6, 7, 8, otherThickness],
  },
  customization: {
    surface: surfaces,
  },
  sensor: {
    diagonal: { min: 7, max: 110 },
  },
} as ISolutionConstraints

export const CoverGlassFixedValues = {
  glass: {
    withGlass: true,
    isDouble: false,
  },
  sensor: {
    withSensor: false,
  },
} as Partial<ISolution>

export const CoverGlassConstraints = {
  glass: {
    thickness: [1, 2, 3, 4, 5, 6, 7, 8, otherThickness],
  },
} as ISolutionConstraints

export interface ISolutionConfig {
  fixedValues: Partial<ISolution>
  constraints: ISolutionConstraints
}

export function getSolutionConfig(productType: ProductType): ISolutionConfig {
  switch (productType) {
    case ProductType.FortouchO:
      return {
        fixedValues: FortouchOFixedValues,
        constraints: FortouchOConstraints,
      }
    case ProductType.FortouchS:
      return {
        fixedValues: FortouchSFixedValues,
        constraints: FortouchSConstraints,
      }
    case ProductType.FortouchC:
      return {
        fixedValues: FortouchCFixedValues,
        constraints: FortouchCConstraints,
      }
  }
}

export function fillOldData(old: ISolution) {
  console.log(old)
  console.log(JSON.stringify(old))
  if (typeof old.glass.height === "number") {
    old.glass.height = [old.glass.height, 0, 0]
  }
  if (typeof old.glass.width === "number") {
    old.glass.width = [old.glass.width, 0, 0]
  }
  if (old.glass.independentWH === undefined) old.glass.independentWH = false

  old.layers = generateLayers(
    old.layers || defaultFormValues.layers,
    old.glass,
    old.sensor,
    old.customization,
    old.product.type
  )

  if ("bl" in old.customization.corners) {
    if ("br" in old.customization.corners)
      if ("tr" in old.customization.corners)
        if ("tl" in old.customization.corners)
          if (typeof old.customization.corners.bl === "string") {
            old.customization.corners.bl = Number(old.customization.corners.bl)
            old.customization.corners.br = Number(old.customization.corners.br)
            old.customization.corners.tl = Number(old.customization.corners.tl)
            old.customization.corners.tr = Number(old.customization.corners.tr)
          }
  } else {
    for (let i = 0; i < 3; i++) {
      if (typeof old.customization.corners[i].bl === "string") {
        old.customization.corners[i].bl = Number(
          old.customization.corners[i].bl
        )
        old.customization.corners[i].br = Number(
          old.customization.corners[i].br
        )
        old.customization.corners[i].tl = Number(
          old.customization.corners[i].tl
        )
        old.customization.corners[i].tr = Number(
          old.customization.corners[i].tr
        )
      }
    }
  }

  old.printing.objects.forEach((oldObj, i) => {
    if (i >= old.printing.objects.length) return
    if (oldObj.img64 === undefined) old.printing.objects[i].img64 = null
    if (oldObj.imgFileName === undefined)
      old.printing.objects[i].imgFileName = null
  })

  if (old.sensor.offset === undefined) {
    old.sensor.offset = { x: 0, y: 0 }
  }

  if ("left" in old.sensor.passiveArea) {
    old.sensor.passiveArea.l = (old.sensor.passiveArea as any).left
    old.sensor.passiveArea.r = (old.sensor.passiveArea as any).right
    old.sensor.passiveArea.b = (old.sensor.passiveArea as any).bottom
    old.sensor.passiveArea.t = (old.sensor.passiveArea as any).top
  }
  if (old.sensor.thickness === undefined) {
    old.sensor.thickness = 0.3
  }

  console.log(old)
  console.log(JSON.stringify(old))
}
